import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import {
	ToolbarBlog,
	copyToClipboard,
	getSocialMediaPlainTextContentFromRawRichContent,
} from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { AddMediaEmptyState } from '../../../components-v2/AddMediaEmptyState';
import { AiButton } from '../../../components-v2/AiButton';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../components-v2/Dropdown';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { TabValue } from '../../../components-v2/UnifiedImageDialog/types';
import { ImagePreview } from '../../../components/ImagePreview';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { TextInput } from '../../../components/TextInput';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../components/richContent/RichContentDocumentEditor';
import { ImageStage, REDUCER_ACTION_KEYS, useBlogPostEditorContext } from './hooks';
import { styleSheet } from './styles';

const defaultEditorConfig: IRichContentDocumentEditorConfig = {
	plugins: 'autolink',
	toolbar: ToolbarBlog,
	validStyles: { ['*']: '' }, // don't allow any styles
};

export interface IProps extends IImpersonationContextComponentProps {
	onAIAssitantLinkClick?: () => void;
	onEditorLoad?: () => void;
}

function BlogPostEditorBase({ onAIAssitantLinkClick, impersonationContext, onEditorLoad }: IProps) {
	const { state, dispatch } = useBlogPostEditorContext();
	const userSession = useUserSession();

	// Unified Image Dialog state
	const [unifiedDialogOpen, setUnifiedDialogOpen] = React.useState(false);
	const [unifiedDialogTab, setUnifiedDialogTab] = React.useState<TabValue>('upload');

	const modalContent = React.useMemo(() => {
		const _content = state.content.getRawRichTextContent();
		// remove all tags to use as content for the image search modal
		const contentString = _content.document.replace(/<[^>]*>?/gm, '');
		return contentString;
	}, [state.content]);

	const hasImageSelected = Boolean(state.selectedImage);

	// Unified dialog handlers
	const handleOpenImageModal = React.useCallback((tab: TabValue = 'upload') => {
		setUnifiedDialogTab(tab);
		setUnifiedDialogOpen(true);
	}, []);

	const handleOpenAIEdit = React.useCallback(() => {
		handleOpenImageModal('ai-edit');
	}, [handleOpenImageModal]);

	const handleImageSelected = React.useCallback(
		(image: Api.IFileAttachment) => {
			dispatch({ type: REDUCER_ACTION_KEYS.IMAGE_ATTACH_SUCCESS, payload: { image } });
			setUnifiedDialogOpen(false);
		},
		[dispatch]
	);

	React.useEffect(() => {
		if (
			state.content.getRawRichTextContent().document.length &&
			!state.selectedImage &&
			state.imageState === ImageStage.Loading
		) {
			handleOpenImageModal('free-images');
			toast.success('Select a blog image');
		}
	}, [state.selectedImage, state.imageState, state.content, handleOpenImageModal]);

	const resetImage = () => {
		dispatch({ type: REDUCER_ACTION_KEYS.IMAGE_SELECTED, payload: { image: null } });
	};

	const onContentEditorStateChanged = (contentEditorState: Api.IRichContentEditorState) => {
		dispatch({ type: REDUCER_ACTION_KEYS.SET_CONTENT, payload: { content: contentEditorState } });
	};

	const handleCopyAsHTML = React.useCallback(async () => {
		const rawContent = state.content.getRawRichTextContent();
		const htmlContent = Api.getContentHtmlStringValueFromRawRichTextContentState(rawContent);
		const success = await copyToClipboard(htmlContent);
		if (success) {
			toast.success('HTML content copied to clipboard');
		} else {
			toast.error('Failed to copy content');
		}
	}, [state.content]);

	const handleCopyAsText = React.useCallback(async () => {
		const rawContent = state.content.getRawRichTextContent();
		const plainText = getSocialMediaPlainTextContentFromRawRichContent(rawContent);
		const success = await copyToClipboard(plainText);
		if (success) {
			toast.success('Plain text content copied to clipboard');
		} else {
			toast.error('Failed to copy content');
		}
	}, [state.content]);

	return (
		<>
			{state.status !== Api.BlogStatus.Pending ? (
				<>
					{onAIAssitantLinkClick && userSession.account?.features?.contentGeneration?.enabled ? (
						<div className={css(styleSheet.aiAssistantWrap)}>
							<AiButton onClick={onAIAssitantLinkClick} title='Write blog with AI' size='xs' />
						</div>
					) : null}
				</>
			) : null}
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='title'>
					Title <sup>*</sup>
				</label>
				<TextInput
					inputId='title'
					name='title'
					type='text'
					value={state.title}
					onChange={e => dispatch({ type: REDUCER_ACTION_KEYS.SET_TITLE, payload: { title: e.target.value } })}
				/>
			</div>
			<div className={css(styleSheet.formControl, styleSheet.contentBlock)}>
				<div className={css(styleSheet.contentLabelRow)}>
					<label htmlFor='content'>
						Blog Content <sup>*</sup>
					</label>
					<Dropdown>
						<DropdownSelectTrigger styles={[styleSheet.copyDropdownTrigger]}>Copy Content</DropdownSelectTrigger>
						<DropdownContent>
							<DropdownItem onClick={handleCopyAsHTML}>Copy as HTML</DropdownItem>
							<DropdownItem onClick={handleCopyAsText}>Copy as Text</DropdownItem>
						</DropdownContent>
					</Dropdown>
				</div>
				<RichContentDocumentEditor
					className={css(styleSheet.editor)}
					config={defaultEditorConfig}
					contentState={state.content}
					onLoad={onEditorLoad}
					onContentStateChanged={onContentEditorStateChanged}
				/>
			</div>
			<div>
				{!state.imageSuggestingLoading ? (
					<>
						{!hasImageSelected ? (
							<AddMediaEmptyState
								onAddMedia={() => handleOpenImageModal()}
								subtitle='Add an image to display with your blog post'
							/>
						) : (
							<section>
								<h2 className={css(styleSheet.subTitle)}>
									{state.suggestedImage ? 'Ai suggested image' : 'Selected Image'}
								</h2>
								{state.selectedImage ? (
									<ImagePreview
										fileName={state.selectedImage?.fileName}
										fileSize={state.selectedImage?.fileSize}
										source={state.selectedImage?.source}
										onRemove={resetImage}
										onEdit={handleOpenAIEdit}
										url={state.selectedImage?.url}
									/>
								) : null}
							</section>
						)}
					</>
				) : (
					<section>
						<figure className={css(styleSheet.loadingImages)}>
							<LoadingSpinner type='large' />{' '}
							<figcaption style={{ marginLeft: 8 }}>Loading suggested images...</figcaption>
						</figure>
					</section>
				)}
			</div>
			<UnifiedImageDialog
				open={unifiedDialogOpen}
				onOpenChange={setUnifiedDialogOpen}
				initialTab={unifiedDialogTab}
				initialImage={state.selectedImage || undefined}
				onImageSelected={handleImageSelected}
				impersonationContext={impersonationContext}
				postContent={modalContent}
			/>
		</>
	);
}

export const BlogPostEditor = inject(ImpersonationContextKey)(observer(BlogPostEditorBase));
